import * as DialogPrimitive from '@radix-ui/react-dialog';
import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import iso3166 from 'iso3166';
import {
  DeliveryProcessingTimeUnit,
  DeliveryRateType,
  SetDeliveryProfileInput,
  ShippingCountryOfOriginOption,
} from '@shopify-multiseller/graphql/partner/types';
import { FormSelect, FormRadioGroup, FormCheckbox, FormInput } from '../form';
import { Alert, Badge, Button, DialogOverlay, MultiSelect } from '../primitives';
import { useDeliveryProfile } from './DeliveryProfileProvider';
import { useSettings } from '../../hooks/useSettings';
import { useAuth, useConfig } from '../../hooks';
import { DeliveryRateFields } from './DeliveryRateFields';
import {
  DeliveryProfileForm,
  OptionItem,
  ProcessingTimePreset,
  defaultForm,
  formValuesToProfileInput,
  processingTimePresets,
  profileToFormValues,
} from './mappers';

export interface IDeliveryProfileEditorProps {
  open?: boolean;
  onClose: () => void;
  deliveryProfileId?: string;
  onCreate?: (_profile: SetDeliveryProfileInput) => void;
  onUpdate?: (_profile: SetDeliveryProfileInput) => void;
  error?: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().when('saveProfile', {
    is: true,
    then: Yup.string()
      .required('Profile name is required')
      .min(8, 'Profile name must be at least 8 characters')
      .max(30, 'Profile name must be at most 30 characters'),
  }),
  originCountryCode: Yup.string().required('Country of origin is required'),
  processingTime: Yup.object().shape({
    min: Yup.number()
      .required('Minimum processing time is required')
      .min(1, 'Minimum processing time must be at least 1'),
    max: Yup.number()
      .required('Maximum processing time is required')
      .when('min', (min, schema) => schema.moreThan(min, 'Maximum must be greater than minimum')),
    unit: Yup.string().required('Processing time unit is required'),
    preset: Yup.string().required('Processing time is required'),
  }),
  default: Yup.object().shape({
    type: Yup.string().required('Rate type is required'),
    rate: Yup.number().when('type', {
      is: DeliveryRateType.FixedPrice,
      then: Yup.number().required('Rate is required').min(0),
    }),
  }),
  otherInternational: Yup.object()
    .shape({
      type: Yup.string().required('Rate type is required'),
      rate: Yup.number().when('type', {
        is: DeliveryRateType.FixedPrice,
        then: Yup.number().required('Rate is required').min(0),
      }),
    })
    .optional()
    .default(undefined),
  zones: Yup.array().of(
    Yup.object().shape({
      countries: Yup.array()
        .min(1, 'At least one country must be selected')
        .required('Countries are required'),
      type: Yup.string().required('Rate type is required'),
      rate: Yup.number().when('type', {
        is: DeliveryRateType.FixedPrice,
        then: Yup.number().required('Rate is required').min(0),
      }),
    }),
  ),
});

/**
 * One question the profile answers, with its controls under it.
 *
 * These were gutter rows — the section name in a 35% column, the fields in the
 * remaining 65% — inside a 650px dialog, which left the fields about 400px
 * wide and the explanations wrapping to four lines. Review asked for the same
 * change across settings: the name and its explanation sit above the fields.
 */
const Section: React.FC<{
  title: string;
  description: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <section className="border-t border-border/60 py-5 first:border-t-0 first:pt-0">
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
    <div className="mt-3">{children}</div>
  </section>
);

/** A rate's own frame, with room for the control that removes it. */
const RateBox: React.FC<{
  onRemove?: () => void;
  removeLabel?: string;
  children: React.ReactNode;
}> = ({ onRemove, removeLabel, children }) => (
  <div className="relative rounded-lg border border-border p-4">
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-3 top-3 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Trash2 className="h-4 w-4" />
        <span className="sr-only">{removeLabel}</span>
      </button>
    )}
    {children}
  </div>
);

const AddButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:text-primary/80"
  >
    <Plus className="h-4 w-4" />
    <span>{children}</span>
  </button>
);

/**
 * The overlay a seller sets a delivery profile up in.
 *
 * Rebuilt on the design system's dialog and tokens. It was the last component
 * in the app carrying its own stylesheet — a hundred and eighty lines of
 * `.deliveryprofileeditor-*` rules with `background-color: white` and
 * `text-red-600` written in by hand, so it did not follow the shop's theme and
 * did not match anything around it.
 */
export const DeliveryProfileEditor: React.FC<
  React.PropsWithChildren<IDeliveryProfileEditorProps>
> = ({ open = false, onClose, deliveryProfileId, onCreate, onUpdate, error }) => {
  const [initialValues, setInitialValues] = useState<DeliveryProfileForm>(defaultForm);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showShadow, setShowShadow] = useState<boolean>(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const deliveryProfileContext = useDeliveryProfile();
  const settings = useSettings();
  const auth = useAuth();
  const config = useConfig();

  // Editing an existing profile has to fetch it first. The editor used to hold
  // a `loading` flag of its own that was hardcoded to false, so the fetch
  // happened behind an empty form and the seller watched their own settings
  // appear a moment after the dialog did. The provider already knows; ask it.
  // Not simply `context.loading` — that is also true while saving, and
  // swapping the whole dialog for a spinner mid-save would throw away the
  // form. Only the initial fetch has an id but no profile yet.
  const loading =
    !!deliveryProfileId && !deliveryProfileContext.deliveryProfile && deliveryProfileContext.loading;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const hasReachedBottom =
      Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 1;
    setShowShadow(!hasReachedBottom);
  };

  // Scroll the error alert (rendered at the top of the body) into view.
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [error]);

  useEffect(() => {
    if (open) {
      if (deliveryProfileId && deliveryProfileContext.deliveryProfile) {
        setInitialValues(profileToFormValues(deliveryProfileContext.deliveryProfile));
      } else {
        setInitialValues(defaultForm);
      }
    }
  }, [open, deliveryProfileId, deliveryProfileContext.deliveryProfile]);

  const handleOnSubmit = async (values: DeliveryProfileForm) => {
    if (!values.originCountryCode) {
      return;
    }
    setIsSaving(true);

    const profile = formValuesToProfileInput(values, deliveryProfileId);

    if (deliveryProfileId) {
      await onUpdate?.(profile);
    } else {
      await onCreate?.(profile);
    }
    setIsSaving(false);
  };

  let countryOptions: OptionItem[] = Object.entries(iso3166).map(([key, value]) => ({
    label: value.name,
    value: key,
  }));

  if (!settings.productShipping.allowAllInternationalShippingCountries) {
    countryOptions = countryOptions.filter((country) =>
      settings.productShipping.allowedInternationalShippingCountries.includes(country.value),
    );
  }

  let countryofOriginOptions: OptionItem[] = Object.entries(iso3166).map(([key, value]) => ({
    label: value.name,
    value: key,
  }));

  switch (settings.productShipping.shippingCountryOfOriginOption) {
    case ShippingCountryOfOriginOption.CustomList:
      countryofOriginOptions = countryofOriginOptions.filter((country) =>
        settings.productShipping.customShippingCountryOfOriginList.includes(country.value),
      );
      break;
    case ShippingCountryOfOriginOption.ShopCountryOnly:
      countryofOriginOptions = countryofOriginOptions.filter(
        (country) => country.value === config?.shopConfig?.shop?.countryCode,
      );
      break;
    case ShippingCountryOfOriginOption.SellerCountryOnly:
      countryofOriginOptions = countryofOriginOptions.filter(
        (country) => country.value === auth?.user?.seller?.address?.countryCode,
      );
      break;
    case ShippingCountryOfOriginOption.AllCountries:
      break;
  }

  const timeUnitOptions = [
    { label: 'Days', value: DeliveryProcessingTimeUnit.Day },
    { label: 'Weeks', value: DeliveryProcessingTimeUnit.Week },
  ];

  const getAvailableCountryOptions = (currentRateIndex: number, values: DeliveryProfileForm) => {
    // Get all selected countries from other rates
    const selectedCountries = new Set<string>();
    values.zones.forEach((rate, index) => {
      if (index !== currentRateIndex) {
        rate.countries.forEach((country) => {
          selectedCountries.add(country.value);
        });
      }
    });

    // Return only countries that haven't been selected in other rates
    return countryOptions.filter((option) => !selectedCountries.has(option.value));
  };

  const handleProcessingTimePresetChange = (
    preset: ProcessingTimePreset,
    setFieldValue: (_field: string, _value: any) => void,
  ) => {
    setFieldValue('processingTime.preset', preset.value);
    if (preset.value !== 'custom' && preset.min !== undefined && preset.max !== undefined) {
      setFieldValue('processingTime.min', preset.min);
      setFieldValue('processingTime.max', preset.max);
      // Always set unit to Day for presets as they are defined in business days
      setFieldValue('processingTime.unit', DeliveryProcessingTimeUnit.Day);
    }
  };

  const title = deliveryProfileId
    ? deliveryProfileContext.deliveryProfile?.name || 'Edit delivery profile'
    : 'Create delivery profile';

  /** Overlay, panel and close control, shared by the loading and form states. */
  const shell = (children: React.ReactNode, describedBy?: string) => (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogPrimitive.Content
          aria-describedby={describedBy}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[86vh] w-[min(44rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl focus:outline-none"
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );

  const header = (
    <div className="flex items-start gap-3 border-b border-border px-6 py-4">
      <div className="min-w-0">
        <DialogPrimitive.Title asChild>
          <h2 className="truncate text-base font-semibold text-foreground">{title}</h2>
        </DialogPrimitive.Title>
        <p id="delivery-profile-purpose" className="mt-0.5 text-xs text-muted-foreground">
          Where you ship from, how long you take, and what you charge. Apply it to as many products
          as you like.
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="ml-auto rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );

  if (loading) {
    return shell(
      <>
        {header}
        <div
          className="flex h-48 items-center justify-center gap-2 text-sm text-muted-foreground"
          data-testid="delivery-profile-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          Loading profile…
        </div>
      </>,
      'delivery-profile-purpose',
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, isValid, submitCount }) =>
        shell(
          <>
            {header}

            <div
              ref={bodyRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto px-6 py-5"
              data-testid="delivery-profile-body"
            >
              {error && (
                <div className="mb-5">
                  <Alert variant="destructive">{error}</Alert>
                </div>
              )}

              <Form id="delivery-profile-form">
                <fieldset disabled={isSaving} className="min-w-0">
                  <Section
                    title="Country of origin"
                    description="Where these products ship from. Customs and delivery estimates use it."
                  >
                    <FormSelect
                      name="originCountryCode"
                      label="Country"
                      options={countryofOriginOptions}
                      placeholder="Select a country"
                    />
                  </Section>

                  <Section
                    title="Processing time"
                    description="How long you take to get an order packed and handed over, before it travels."
                  >
                    <div className="space-y-3">
                      <div className="sm:max-w-xs">
                        <FormSelect
                          name="processingTime.preset"
                          label="Processing time"
                          options={processingTimePresets.map((preset) => ({
                            label: preset.label,
                            value: preset.value,
                          }))}
                          onValueChange={(value) => {
                            const preset = processingTimePresets.find((p) => p.value === value);
                            if (preset) {
                              handleProcessingTimePresetChange(preset, setFieldValue);
                            }
                          }}
                        />
                      </div>
                      {values.processingTime.preset === 'custom' && (
                        <div className="grid gap-3 sm:grid-cols-3">
                          <FormInput
                            name="processingTime.min"
                            label="Minimum"
                            type="number"
                            min={0}
                          />
                          <FormInput
                            name="processingTime.max"
                            label="Maximum"
                            type="number"
                            min={0}
                          />
                          <FormRadioGroup
                            name="processingTime.unit"
                            label="Unit"
                            options={timeUnitOptions}
                          />
                        </div>
                      )}
                    </div>
                  </Section>

                  <Section
                    title="Domestic rate"
                    description={`What you charge to deliver inside ${
                      countryofOriginOptions.find((c) => c.value === values.originCountryCode)
                        ?.label ?? 'your country of origin'
                    }.`}
                  >
                    <RateBox>
                      <DeliveryRateFields name="default" label="Domestic rate" />
                    </RateBox>
                  </Section>

                  <Section
                    title="International rate"
                    description="What you charge everywhere else. Leave it off and you only deliver to the countries listed below."
                  >
                    {values.otherInternational ? (
                      <RateBox
                        removeLabel="Remove the international rate"
                        onRemove={() => setFieldValue('otherInternational', undefined)}
                      >
                        <DeliveryRateFields name="otherInternational" label="International rate" />
                      </RateBox>
                    ) : (
                      <AddButton
                        onClick={() =>
                          setFieldValue('otherInternational', {
                            type: DeliveryRateType.FreeDelivery,
                            rate: undefined,
                            additionalItemRate: undefined,
                          })
                        }
                      >
                        Add an international rate
                      </AddButton>
                    )}
                  </Section>

                  <Section
                    title="Zones"
                    description="A different price for particular countries. Each country can appear in one zone only."
                  >
                    <div className="space-y-4">
                      {values.zones.map((rate, index) => (
                        <RateBox
                          key={rate.id}
                          removeLabel={`Remove zone ${index + 1}`}
                          onRemove={() => {
                            const newRates = [...values.zones];
                            newRates.splice(index, 1);
                            setFieldValue('zones', newRates);
                          }}
                        >
                          {/* Numbered, because a zone has no name of its own —
                              it is the countries it covers, and until one is
                              chosen there is nothing on screen to call it. */}
                          <p className="mb-2 pr-8 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Zone {index + 1}
                          </p>
                          <div className="space-y-3">
                            <MultiSelect
                              aria-label={`Countries in zone ${index + 1}`}
                              placeholder="Select countries"
                              options={getAvailableCountryOptions(index, values)}
                              selected={rate.countries.map((c) => c.value)}
                              onSelectedChange={(vals) =>
                                setFieldValue(
                                  `zones.${index}.countries`,
                                  vals.map(
                                    (v) =>
                                      countryOptions.find((o) => o.value === v) ?? {
                                        value: v,
                                        label: v,
                                      },
                                  ),
                                )
                              }
                            />
                            {rate.countries.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {rate.countries.map((country) => (
                                  <Badge key={country.value} variant="neutral" className="gap-1">
                                    {countryOptions.find((c) => c.value === country.value)?.label ||
                                      country.value}
                                    <button
                                      type="button"
                                      className="rounded-sm hover:text-foreground"
                                      onClick={() => {
                                        const newCountries = rate.countries.filter(
                                          (c) => c.value !== country.value,
                                        );
                                        setFieldValue(`zones.${index}.countries`, newCountries);
                                      }}
                                    >
                                      <span className="sr-only">Remove</span>
                                      <X className="h-3 w-3" />
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                            <DeliveryRateFields
                              name={`zones.${index}`}
                              label={`Zone ${index + 1} rate`}
                            />
                          </div>
                        </RateBox>
                      ))}

                      <AddButton
                        onClick={() => {
                          const newRate = {
                            id: Math.random().toString(36).substring(2, 11),
                            countries: [] as OptionItem[],
                            type: DeliveryRateType.FreeDelivery,
                            rate: undefined,
                            additionalItemRate: undefined,
                          };
                          setFieldValue('zones', [...values.zones, newRate]);
                        }}
                      >
                        Add a zone
                      </AddButton>
                    </div>
                  </Section>

                  {(!deliveryProfileId || !deliveryProfileContext.deliveryProfile?.name) && (
                    <Section
                      title="Reuse this setup"
                      description="Saved profiles can be applied to any product without setting the rates up again."
                    >
                      <FormCheckbox name="saveProfile" label="Save as a delivery profile" />
                      {values.saveProfile && (
                        <div className="mt-3 sm:max-w-sm">
                          <FormInput
                            name="name"
                            label="Profile name"
                            placeholder="Coastal parcels"
                          />
                        </div>
                      )}
                    </Section>
                  )}
                </fieldset>
              </Form>
            </div>

            {/* A hairline of shadow while there is more below, so a body that
                scrolls does not end flush against the footer as if it stopped. */}
            {showShadow && (
              <div className="pointer-events-none -mt-8 h-8 bg-gradient-to-t from-foreground/10 to-transparent" />
            )}
            <div className="flex items-center gap-3 border-t border-border bg-muted/40 px-6 py-3">
              {/* The same sentence the save bar uses, with the same count:
                  "Please resolve validation errors" is the machine's word for
                  it, says nothing about how much is wrong, and was the only
                  place in the app phrasing it that way. */}
              {submitCount > 0 && Object.keys(errors).length > 0 && (
                <p className="text-sm text-destructive">
                  {`Fix ${Object.keys(errors).length} ${
                    Object.keys(errors).length === 1 ? 'error' : 'errors'
                  } to save`}
                </p>
              )}
              <div className="ml-auto flex gap-2">
                {!isSaving && (
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  form="delivery-profile-form"
                  disabled={isSaving || (submitCount > 0 && !isValid)}
                  loading={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </>,
          'delivery-profile-purpose',
        )
      }
    </Formik>
  );
};
